import { z } from "zod";

const JsonRecord = z.record(z.string(), z.unknown());

const StudyReportTestSchema = z.object({
  id: z.number().int(),
  name: z.string().nullable(),
  track_code: z.string(),
  source_event: z.string().nullable(),
  baseline_setup_name: z.string().nullable(),
});

const StudyReportObjectiveSchema = z.object({
  objective_id: z.string().nullable(),
  display_name: z.string().nullable(),
  primary_kpi_id: z.string().nullable(),
  direction: z.enum(["min", "max", "target"]).nullable(),
  trust_level: z.enum(["trusted", "experimental", "unavailable"]).nullable(),
  baseline_relative: z.boolean().default(false),
});

const StudyReportSessionSchema = z.object({
  id: z.number().int(),
  state: z.enum(["running", "stopped", "completed", "failed"]),
  generator: z.enum(["click_sweep", "claude"]),
  max_runs: z.number().int(),
  runs_total: z.number().int(),
  runs_terminal: z.number().int(),
  best_run_id: z.number().int().nullable(),
  best_score: z.number().nullable(),
  target_met: z.boolean(),
  model: z.string().nullable(),
  static_prompt_hash: z.string().nullable(),
  total_input_tokens: z.number().int(),
  total_output_tokens: z.number().int(),
  total_cache_read_tokens: z.number().int(),
  total_cache_creation_tokens: z.number().int(),
  estimated_cost_usd: z.number().nullable(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
  error: z.string().nullable(),
});

const StudyReportConvergencePointSchema = z.object({
  run_id: z.number().int(),
  run_number: z.number().int(),
  proposal_index: z.number().int().nullable(),
  status: z.enum(["pending", "running", "complete", "failed", "cancelled"]),
  score: z.number().nullable(),
  best_score: z.number().nullable(),
  improved: z.boolean(),
  decision: z.string().nullable(),
  reasoning_summary: z.string().nullable(),
  created_at: z.string().nullable(),
});

const StudyReportStepSchema = z.object({
  id: z.number().int(),
  ts: z.string(),
  kind: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
  run_id: z.number().int().nullable(),
  proposal_index: z.number().int().nullable(),
  payload: JsonRecord,
});

const StudyReportRunConfigurationSchema = z.object({
  run_id: z.number().int(),
  run_number: z.number().int(),
  proposal_index: z.number().int().nullable(),
  source: z.string(),
  status: z.enum(["pending", "running", "complete", "failed", "cancelled"]),
  damper_setting: JsonRecord,
  damper_setting_provenance: JsonRecord.nullable(),
  kpi_scores: JsonRecord.nullable(),
  objective_score: z.number().nullable(),
  objective_decision: z.string().nullable(),
  objective_explanation: z.string().nullable(),
  reasoning_summary: z.string().nullable(),
});

export const StudyReportSchema = z.object({
  status: z.enum(["no_session", "running", "completed", "stopped", "failed"]),
  generated_at: z.string(),
  test: StudyReportTestSchema,
  session: StudyReportSessionSchema.nullable(),
  objective: StudyReportObjectiveSchema.nullable(),
  inputs: JsonRecord.nullable(),
  summary: JsonRecord,
  convergence: z.array(StudyReportConvergencePointSchema),
  steps: z.array(StudyReportStepSchema),
  run_configurations: z.array(StudyReportRunConfigurationSchema),
});

export type StudyReport = z.infer<typeof StudyReportSchema>;
export type StudyReportConvergencePoint =
  z.infer<typeof StudyReportConvergencePointSchema>;
export type StudyReportRunConfiguration =
  z.infer<typeof StudyReportRunConfigurationSchema>;

export async function fetchStudyReport(testId: number): Promise<StudyReport> {
  const r = await fetch(`/api/tests/${testId}/study_report`);
  if (!r.ok) {
    throw new Error(`GET /api/tests/${testId}/study_report failed: ${r.status}`);
  }
  return StudyReportSchema.parse(await r.json());
}
